import React from 'react'
import { Dialog as MuiDialog, DialogTitle, DialogContent, useTheme, Box } from '@mui/material'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { Typography } from '../Typography'
import { makeStyles } from 'tss-react/mui'
import Actions from './Actions'

const useStyles = makeStyles()(theme => ({
  dialog: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 5,
      paddingTop: 5,
    },
    height: '100%',
    '& .MuiDialog-paper.MuiPaper-root': {
      padding: 0,
      overflowX: 'hidden',
    },
    backdropFilter: 'blur(0)',
  },
  dialogTitle: {
    minHeight: theme.shape.dialog.title.minHeight,
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
    paddingRight: theme.spacing(8),
    [`${theme.breakpoints.down('md')} and (hover: none)`]: {
      marginTop: 0,
      paddingTop: 0,
    },
  },
  title: {
    position: 'relative',
    marginLeft: theme.spacing(2),
    color: theme.palette.text.paperTitle,
    '&::before': {
      content: '""',
      display: 'block',
      width: 4,
      height: `calc(100% + ${theme.spacing(4)})`,
      backgroundColor: theme.palette.background.paperAccent,
      position: 'absolute',
      top: `-${theme.spacing(3)}`,
      left: `-${theme.spacing(2)}`,
    },
  },
  dialogDivider: {
    borderTop: '2px solid',
    borderColor: theme.palette.grey.grey06,
    margin: `0 ${theme.shape.dialogDivider}px`,
  },
}))

interface ConfirmationDialogProps {
  text?: string
  title: string
  buttonText?: [string, string]
  onConfirm: (onClose: () => void) => void
  onCancel?: (onClose: () => void) => void
  onClose: () => void
  open?: boolean
  isLoading?: boolean
  renderAdditionalButtonContent?: (onClose: () => void) => React.ReactElement
  children?: React.ReactNode
  showChildrenOnTop?: boolean
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  title,
  text,
  buttonText,
  onConfirm,
  onClose,
  onCancel,
  open = true,
  isLoading = false,
  renderAdditionalButtonContent,
  children,
  showChildrenOnTop,
}) => {
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const handleCancel = (): void => {
    if (onCancel) {
      onCancel(onClose)
    } else {
      onClose()
    }
  }

  return (
    <MuiDialog open={open} onClose={handleCancel} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle} component={'div'}>
        <Typography variant={theme.typography.dialog} className={classes.title} color="textPrimary">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 4, mb: 4 }}>
        {children !== undefined && showChildrenOnTop && <>{children}</>}
        <Typography variant="body1">{text}</Typography>
        {children !== undefined && !showChildrenOnTop && <>{children}</>}
      </DialogContent>

      <Box className={classes.dialogDivider} />

      <Actions
        setOpen={onClose}
        actions={{
          primary: {
            name: buttonText?.[1] ?? t('common.yes'),
            onClick: () => {
              onConfirm(onClose)
            },
            isLoading: isLoading,
          },
          secondary: [
            {
              name: buttonText?.[0] ?? t('common.no'),
              onClick: handleCancel,
            },
          ],
        }}
        additionalActionsLeft={renderAdditionalButtonContent?.(onClose)}
      />
    </MuiDialog>
  )
}

export default ConfirmationDialog
